import omit from 'lodash/omit';

import type {MetricEntry, QueryMetricEntry} from 'common/query-builder/query-entry-section/types';
import {getShowSection} from 'common/report/insights/util/events';
import {BehaviorShowClause, FormulaShowClause, ShowClauseFormula} from 'common/report/insights/util/types';
import {getAlphaLabelFromIndex} from 'common/util';
import {Metric} from 'app_api/projects/metrics/types';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {getResolvedFormulaShowClause, isModifiedShowClauseFormula} from 'common/util/formulas';
import {ModelStatus} from 'common/util/model-resource/model-resource';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {getUserAccess} from 'common/report/util/saved-entities';

import {get_parser as getFormulaParser, Token, UnexpectedCharacters, UnexpectedToken} from './formula-parser-compiled';

const DefaultFormulaNamePrefix = `Formula`;

export function getNextFormulaName({existingFormulaNames}: {existingFormulaNames: string[]}): string {
  const getFormulaNumber = () => {
    const defaultFormulaNameRegex = RegExp(`^${DefaultFormulaNamePrefix} (\\d+)$`);
    const existingFormulaNumbers = existingFormulaNames
      .map((name) => name.match(defaultFormulaNameRegex)?.[1])
      .filter((numStr) => numStr)
      .map((numStr) => Number(numStr));
    return Math.max(existingFormulaNames.length, ...existingFormulaNumbers) + 1;
  };

  return existingFormulaNames.length === 0
    ? DefaultFormulaNamePrefix
    : `${DefaultFormulaNamePrefix} ${getFormulaNumber()}`;
}

export function getFormulaShowClauseTrackingProps({
  formulaShowClause,
  metricsResource,
}: {
  formulaShowClause: FormulaShowClause;
  metricsResource: MetricsResource;
}) {
  const resolvedFormula = getResolvedFormulaShowClause({metricsResource, formulaShowClause});
  const metric = metricsResource.getOne(resolvedFormula.formula.id);
  const operatorsCount = (resolvedFormula.formula.definition?.match(/\*|\/|\+|-/g) || []).length;
  const operandsCount = (resolvedFormula.formula.definition?.match(/[A-Z]+/g) || []).length;

  return {
    name: resolvedFormula.formula.name,
    definition: resolvedFormula.formula.definition,
    id: resolvedFormula.formula.id,
    'is expanded': formulaShowClause.isExpanded,
    'is hidden': formulaShowClause.isHidden,
    'is cumulative': Boolean(formulaShowClause.measurement?.cumulative),
    'is rolling': Boolean(formulaShowClause.measurement?.rolling),
    'is edited': isModifiedShowClauseFormula(formulaShowClause.formula),
    '# of metrics': resolvedFormula.formula.referencedMetrics?.length,
    'types of metrics': Array.from(
      new Set(resolvedFormula.formula.referencedMetrics?.map((metric) => metric.behavior.type)),
    ),
    '# of operators': operatorsCount,
    '# of operands': operandsCount,
    ...(metric.status === ModelStatus.Success
      ? {
          'permission level': getUserAccess(metric, {
            ownerId: metric.value.created_by.id,
            userId: userCookieUtils.getUserIdentityFromCookie()?.id,
          }),
        }
      : {}),
  };
}

/**
 * For a formula and all the metrics it references, we need to map
 * existing positions in the report entries to what they would become
 * inside a saved formula referenced metrics array.
 *
 * For example, if a report has 3 metrics and an inline formula only references metric `C`,
 * when saving this formula, the variable `C` will now become `A` and this metric will
 * be index 0 in the `referencedMetrics` field of the saved formula
 */
export function getUpdatedFormulaAndMetrics(
  formula: ShowClauseFormula,
  entries: Array<QueryMetricEntry | MetricEntry>,
): {definition: string; referencedMetrics: BehaviorShowClause[]} {
  const formulaOperands = new Set(formula.definition.match(/[A-Z]{1,}/g));

  // a map of the metrics referenced by the inline formula
  // to the variable identifier they are currently using
  const referencedEntries: Array<QueryMetricEntry | MetricEntry> = [];
  const updatedFormulaMap: Record<string, string> = {};

  for (const [index, entry] of entries.entries()) {
    const identifier = getAlphaLabelFromIndex(index);
    if (formulaOperands.has(identifier)) {
      const newIdentifier = getAlphaLabelFromIndex(referencedEntries.length);
      referencedEntries.push({
        ...entry,
        dataGroupId: null,
      });
      updatedFormulaMap[identifier] = newIdentifier;
    }
  }

  // saving a constant formula
  if (referencedEntries.length === 0) {
    return {
      definition: formula.definition,
      referencedMetrics: [],
    };
  }

  return {
    definition: replaceFormulaReferences(formula.definition, updatedFormulaMap),
    referencedMetrics: getReferencedMetricsFromEntries(referencedEntries),
  };
}

export function getReferencedMetricsFromEntries(entries: Array<QueryMetricEntry | MetricEntry>): BehaviorShowClause[] {
  return getShowSection({entries})
    .toBookmarkData()
    .map((metric) => omit(metric, `isHidden`, `isExpanded`));
}

export class FormulaParseError extends Error {}
export class FormulaParseVariableError extends FormulaParseError {
  constructor(public variable: string) {
    super(`Unknown variable: ${variable}`);
  }
}

let _parser;

/**
 * Parses a formula and returns the AST.
 */
export function parseFormula(formula: string, availableVariables: string[]) {
  try {
    _parser ||= getFormulaParser();

    const tree = _parser.parse(formula);

    // ensure only variables that are available are used
    for (const token of tree.scan_values((node) => node instanceof Token && node.type === `VAR`)) {
      if (!availableVariables.includes(token.value)) {
        throw new FormulaParseVariableError(token.value);
      }
    }

    return tree;
  } catch (err) {
    if (err instanceof UnexpectedToken) {
      if (err.token.type === `$END`) {
        throw new FormulaParseError(`Incomplete formula`);
      }

      throw new FormulaParseError(`Unexpected token: ${err.token.value}`);
    } else if (err instanceof UnexpectedCharacters) {
      throw new FormulaParseError(`Unexpected character: ${err.char}`);
    }

    throw err;
  }
}

export function getMetricUpdateFromFormulaShowClause(clause: FormulaShowClause): Metric {
  return {
    type: `formula`,
    id: clause.formula.id,
    name: clause.formula.name,
    definition: {
      formula: {
        definition: clause.formula.definition,
        referencedMetrics: clause.formula.referencedMetrics,
      },
      measurement: omit(clause.measurement, [`multiAttribution`]),
    },
  };
}

/**
 * Updates a formula definition by replacing old labels with new labels.
 *
 * @param referenceMap - Mapping of old labels to new labels
 */
export function replaceFormulaReferences(formula: string, referenceMap: Record<string, string>) {
  return formula.replace(/\w+/g, (sub) => {
    // if the label is in the reference map, replace it with the new label. otherwise we'll leave it alone
    return referenceMap[sub] || sub;
  });
}
